/**
 * WHAT AN INVOICING ADDRESS SHOWS WHILE INVOICING IS SWITCHED OFF FOR THE COMPANY (C6, audit F31).
 *
 * App.jsx's `ModuleSwitchRoute` holds `/invoicing/*` on the server's switch. An address it held used to land on the
 * portal with nothing said - and Invoicing addresses are reached by links, not only by the (hidden) tile: the Capex
 * billing page's "Open the pay application in Invoicing", a bookmark, an emailed link. So, as a capital estimate's
 * address does with Project Financials off (modules/capex/CapexSwitchedOff.jsx), every Invoicing address says
 * Invoicing is switched off, who can switch it on and where, and gives a way back.
 *
 * ONLY ON A REAL "OFF" (`whenOff`): drawn once the server has answered for this company. When the lookup itself
 * failed, App.jsx hands the route `<InvoicingSwitchedOff couldNotCheck />` as `whenUnknown`, which says this page
 * could not check and offers a reload - never "switched off", which would send an administrator to find it ON.
 *
 * Nothing here reaches the vendored module or its service, so this file is safe to load eagerly from App.jsx.
 */
import { Link } from 'react-router-dom'

import AppHeader from '../../shared/components/AppHeader.jsx'
import { useAuth } from '../../hooks/useAuth.jsx'
import { useT } from '../../shared/i18n/LanguageContext.jsx'

export default function InvoicingSwitchedOff({ couldNotCheck = false }) {
  const { user } = useAuth()
  const tm = useT('modules')
  const name = tm('invoicing') || 'Invoicing'
  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader title={name} user={user} />
      <main className="max-w-xl mx-auto px-4 py-12">
        {couldNotCheck ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3" data-testid="invoicing-switch-not-checked">
            <h1 className="text-xl font-semibold text-gray-900">{name} did not open</h1>
            <p className="text-gray-700">
              This page could not check whether {name} is switched on for this company, so it has not opened it.
            </p>
            <p className="text-gray-700">Reload the page to check again.</p>
            <p className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                Reload
              </button>
              <Link to="/portal" className="text-blue-600 hover:text-blue-700 font-medium">Back to the portal</Link>
            </p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3" data-testid="invoicing-switched-off">
            <h1 className="text-xl font-semibold text-gray-900">{name} is switched off for this company</h1>
            <p className="text-gray-700">
              This address is a page of {name} - pay applications, T&amp;M invoices, receivables and waivers - and{' '}
              {name} is switched off for this company, so it cannot be opened.
            </p>
            <p className="text-gray-700">
              A platform administrator can switch {name} on for this company under the company&apos;s Modules.
            </p>
            <p>
              <Link to="/portal" className="text-blue-600 hover:text-blue-700 font-medium">Back to the portal</Link>
            </p>
          </div>
        )}
      </main>
    </div>
  )
}
